import { useState } from "react"
import { studentRequest } from "services/requests"
import {
  LEADERBOARD_RANGE_TYPES,
  LEADERBOARD_SCOPE_TYPES,
} from "../../pages/games/common/constants"
import { useGlobalAlert } from "contexts/GlobalAlertContext"
import moment from "moment"

const FormData = require("form-data")
const prefix = "games/arcade"

const useApi = () => {
  const [isFetching, setIsFetching] = useState(false)
  const { showAlert } = useGlobalAlert()

  const getCategories = async () => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/categories`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.categories || []
  }

  const getSkills = async () => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/skills`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.skills || []
  }

  const getGames = async ({ categoryId = 0, skillId = 0 }) => {
    setIsFetching(true)
    const params = new URLSearchParams()
    if (categoryId !== undefined && categoryId !== null) {
      params.append("categoryId", categoryId)
    }
    if (skillId !== undefined && skillId !== null) {
      params.append("skillId", skillId)
    }
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/games?${params.toString()}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.games || []
  }

  const getGamePathEducationLevel = async ({
    gameId = 0,
    learningLanguageId = 0,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/path/educationLevel`,
      body: {
        learningLanguageId,
        gameId,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.educationLevels || []
  }

  const getGamePathLevels = async ({
    gameId,
    educationLevelId,
    learningLanguageId,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/path/levels`,
      body: {
        learningLanguageId,
        gameId,
        educationLevelId,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.levels || []
  }

  const getGamePathUnits = async ({ gameId, levelId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/path/units`,
      body: {
        gameId,
        levelId,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.units || []
  }

  const getGameSessionById = async ({ gameSessionId = 0 }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/session?gameSessionId=${gameSessionId}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data || null
  }

  const setIndividualGameSession = async ({
    gameContentReferenceTypeId = 3,
    gameId = 0,
    moduleId = 0,
    metadata = "",
    isRandomOrderEnabled = false,
    learningLanguageId = 3,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/session/individual`,
      body: {
        gameContentReferenceTypeId,
        gameId,
        moduleId,
        metadata,
        isRandomOrderEnabled,
        learningLanguageId,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.gameSession || {}
  }

  const retryIndividualGameSession = async ({ gameSessionId = 0 }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/session/individual/retry?sourceGameSessionId=${gameSessionId}`,
      body: {
        sourceGameSessionId: gameSessionId,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.gameSession || {}
  }

  const setGameSessionStatus = async ({
    gameSessionId,
    gameSessionParticipantId,
    gameSessionStatusId,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/session/status`,
      body: {
        gameSessionId,
        gameSessionParticipantId,
        gameSessionStatusId,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data
  }

  const setGameSessionAnswer = async ({
    gameId = 0,
    gameSessionId,
    gameSessionParticipantId,
    gameSessionContentId,
    isCorrect,
    grade = 0,
    answers = [],
    metadata = "",
    files = [],
    onUploadProgress = () => {},
  }) => {
    setIsFetching(true)
    const data = new FormData()
    data.append("GameId", gameId)
    data.append("GameSessionId", gameSessionId)
    data.append("GameSessionParticipantId", gameSessionParticipantId)
    data.append("GameSessionContentId", gameSessionContentId)
    data.append("IsCorrect", isCorrect)
    data.append("Grade", grade)
    data.append("Metadata", metadata)
    for (let i = 0; i < answers.length; i++) {
      data.append(`Answers[${i}].contentId`, answers[i].contentId)
      data.append(
        `Answers[${i}].gameSessionContentId`,
        answers[i].gameSessionContentId
      )
      data.append(`Answers[${i}].answer`, answers[i].answer)
      data.append(`Answers[${i}].status`, answers[i].status)
      data.append(`Answers[${i}].metadata`, answers[i].metadata)
    }
    for (let i = 0; i < files.length; i++) {
      if (files[i]) {
        data.append(`Files`, files[i])
      }
    }
    const response = await studentRequest.sendFormData({
      endpoint: `${prefix}/game/session/answer`,
      formData: data,
      onUploadProgress,
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data
  }

  const setGameSessionNextContent = async ({
    gameSessionContentId = 0,
    gameSessionId = 0,
    gameId = 0,
    gameSessionParticipantId = 0,
    nextRandomContent = true,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/session/nextContent`,
      body: {
        gameSessionContentId,
        gameSessionId,
        gameId,
        gameSessionParticipantId,
        nextRandomContent,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data
  }

  const getFavorites = async ({ gameId, learningLanguageId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/content/favorite?gameId=${gameId}&learningLanguageId=${learningLanguageId}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.favoriteContent || []
  }

  const addToFavorite = async ({ contentId, gameId, notes = "" }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/content/favorite`,
      body: {
        contentId,
        gameId,
        notes,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.favoriteContent || null
  }

  const deleteFromFavorites = async ({ gameUserFavoriteContentId = 0 }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/content/favorite/delete?gameUserFavoriteContentId=${gameUserFavoriteContentId}`,
      body: null,
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data
  }

  const updateFavorites = async ({
    gameUserFavoriteContentId = 0,
    notes = "",
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/content/favorite/update`,
      body: {
        gameUserFavoriteContentId,
        notes,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data
  }

  const getGameTotalFavorites = async ({
    gameId = 0,
    learningLanguageId = 3,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/content/favorite/total?gameId=${gameId}&learningLanguageId=${learningLanguageId}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data?.total || 0
  }

  const getLeaderboardStats = async ({ groupId = 0 }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/leaderboard/stats?groupId=${groupId}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return (
      response.data?.data || {
        place: 0,
        totalGamesPlayed: 0,
        totalPoints: 0,
      }
    )
  }

  const getLeaderboard = async ({
    learningLanguageId = 0,
    groupId = 0,
    scopeType = 0,
    rangeType = 0,
    date = null,
    pageNumber = 1,
    pageSize = 10,
  }) => {
    const validRangeTypes = Object.values(LEADERBOARD_RANGE_TYPES)
    const validScopeTypes = Object.values(LEADERBOARD_SCOPE_TYPES)
    if (!validRangeTypes.includes(rangeType)) {
      showAlert("Invalid rangeType")
      return {
        entries: [],
        totalCount: 0,
      }
    }
    if (!validScopeTypes.includes(scopeType)) {
      showAlert("Invalid scopeType")
      return {
        entries: [],
        totalCount: 0,
      }
    }
    setIsFetching(true)
    const params = new URLSearchParams()
    params.append("learningLanguageId", learningLanguageId)
    params.append("groupId", groupId)
    params.append("scopeType", scopeType)
    params.append("rangeType", rangeType)
    params.append("pageNumber", pageNumber)
    params.append("pageSize", pageSize)
    const isValidDate = moment(date, "YYYY-MM-DD", true).isValid()
    if (date !== null && isValidDate) {
      params.append("date", date)
    }
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/leaderboard?${params.toString()}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return (
      response.data?.data || {
        entries: [],
        totalCount: 0,
      }
    )
  }

  const getLeaderboardSummary = async ({
    learningLanguageId = 0,
    groupId = 0,
    scopeType = 0,
    rangeType = 0,
    date = null,
    topCount = 3,
  }) => {
    setIsFetching(true)
    const params = new URLSearchParams()
    params.append("learningLanguageId", learningLanguageId)
    params.append("groupId", groupId)
    params.append("scopeType", scopeType)
    params.append("rangeType", rangeType)
    const isValidDate = moment(date, "YYYY-MM-DD", true).isValid()
    if (date !== null && isValidDate) {
      params.append("date", date)
    }
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/leaderboard/summary?${params.toString()}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data || null
  }

  const getSessionHistory = async ({
    learningLanguageId = 0,
    gameId = 0,
    gameCategoryId = 1,
    pageNumber = 1,
    pageSize = 10,
  }) => {
    setIsFetching(true)
    const params = new URLSearchParams()
    params.append("learningLanguageId", learningLanguageId)
    params.append("pageNumber", pageNumber)
    params.append("pageSize", pageSize)
    if (gameId > 0) {
      params.append("gameId", gameId)
    }
    if (gameCategoryId > 0) {
      params.append("gameCategoryId", gameCategoryId)
    }
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/game/session/history?${params.toString()}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return (
      response.data?.data || {
        entries: [],
        totalCount: 0,
      }
    )
  }

  const getLeaderboardDailyChallenge = async ({
    learningLanguageId = 0,
    date = null,
  }) => {
    setIsFetching(true)
    const params = new URLSearchParams()
    params.append("learningLanguageId", learningLanguageId)
    if (date !== null) {
      params.append("date", date)
    }
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/leaderboard/dailyChallenge?${params.toString()}`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data?.data || null
  }

  return {
    getCategories,
    getSkills,
    getGames,
    getGamePathEducationLevel,
    getGamePathLevels,
    getGamePathUnits,
    getGameSessionById,
    setIndividualGameSession,
    setGameSessionStatus,
    setGameSessionAnswer,
    setGameSessionNextContent,
    retryIndividualGameSession,
    getFavorites,
    addToFavorite,
    deleteFromFavorites,
    updateFavorites,
    getGameTotalFavorites,
    getLeaderboardStats,
    getLeaderboard,
    getLeaderboardSummary,
    getSessionHistory,
    getLeaderboardDailyChallenge,
    isFetching,
  }
}

export default useApi
